import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Clock, CalendarDays, Download, MessageCircle, Check, X, Info,
  MapPinned, ChevronLeft, Star, BedDouble,
} from "lucide-react";
import { api, fileUrl, inr } from "@/lib/api";
import { track } from "@/lib/meta";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { CreatorProfileBlock } from "@/components/site/CreatorTrip";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LeadDialog } from "@/components/site/LeadDialog";
import { BookingDialog } from "@/components/site/BookingDialog";
import { WishlistButton } from "@/components/site/WishlistButton";
import { StarRating } from "@/components/site/StarRating";
import { TripReviews } from "@/components/site/Reviews";
import { Seo } from "@/components/site/Seo";

export default function TripDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [trip, setTrip] = useState(null);
  const [active, setActive] = useState(0);
  const [err, setErr] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/trips/${slug}`).then(({ data }) => { setTrip(data); setActive(0); track("ViewContent", { content_ids: [data.id], content_name: data.name, content_type: "product", value: data.starting_price || 0, currency: "INR" }); }).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="pt-40 text-center pb-40"><p className="font-heading text-4xl">Trip not found</p><Link to="/trips"><Button className="mt-4 rounded-full">Back to trips</Button></Link></div>;
  if (!trip) return <div className="pt-40 pb-40 text-center text-muted-foreground">Loading…</div>;

  const gallery = trip.gallery?.length ? trip.gallery : [trip.cover_image];

  const tripLd = {
    "@context": "https://schema.org", "@type": "TouristTrip", name: trip.name, description: trip.overview,
    image: fileUrl(trip.cover_image), touristType: trip.category, itinerary: { "@type": "ItemList", numberOfItems: (trip.itinerary || []).length },
    offers: (trip.departures || []).map((d) => ({ "@type": "Offer", price: d.from_price, priceCurrency: "INR", availability: d.sold_out ? "https://schema.org/SoldOut" : "https://schema.org/InStock", validFrom: d.date })),
    ...(trip.review_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: trip.avg_rating, reviewCount: trip.review_count } } : {}),
  };
  return (
    <div className="pt-16 pb-24 lg:pb-0">
      <Seo title={trip.seo_title || `${trip.name} — ${trip.duration}`} description={trip.seo_description || (trip.overview || "").slice(0, 160)} image={trip.cover_image} type="article" jsonLd={tripLd} />
      {/* Gallery hero */}
      <div className="mx-auto max-w-7xl px-6 pt-6">
        <Link to="/trips" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="back-to-trips"><ChevronLeft className="h-4 w-4" /> All Trips</Link>
        <div className="grid gap-3 lg:grid-cols-4 lg:grid-rows-2 h-auto lg:h-[460px]">
          <motion.div layout className="lg:col-span-3 lg:row-span-2 rounded-2xl overflow-hidden h-72 lg:h-full">
            <img src={fileUrl(gallery[active])} alt={trip.name} className="h-full w-full object-cover" />
            <WishlistButton tripId={trip.id} className="absolute top-4 right-4 z-10 h-11 w-11" />
          </motion.div>
          <div className="lg:col-span-1 lg:row-span-2 grid grid-cols-3 lg:grid-cols-1 gap-3 overflow-hidden">
            {gallery.slice(0, 3).map((g, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                className={`rounded-xl overflow-hidden h-24 lg:h-full transition-opacity ${active === i ? "ring-2 ring-primary" : "opacity-70 hover:opacity-100"}`}>
                <img src={fileUrl(g)} alt="" className="h-full w-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10 grid gap-10 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              <Badge className="capitalize rounded-full">{trip.category?.replace("-", " ")}</Badge>
              {trip.special && <Badge className="rounded-full bg-gold text-black hover:bg-gold" data-testid="detail-special-tag">★ {trip.special_tag || "Safarshala Special"}{trip.special_note ? ` · ${trip.special_note}` : ""}</Badge>}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold leading-tight">{trip.name}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-primary" /> {trip.destination}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-primary" /> {trip.duration}</span>
              {trip.pickup_points?.[0] && <span className="flex items-center gap-1"><MapPinned className="h-4 w-4 text-primary" /> From {trip.pickup_points[0]}</span>}
              {trip.review_count > 0 && (
                <a href="#reviews" className="flex items-center gap-1.5 hover:text-primary" data-testid="detail-rating-summary">
                  <StarRating value={trip.avg_rating} /> <span className="font-medium text-foreground">{trip.avg_rating}</span> ({trip.review_count} reviews)
                </a>
              )}
            </div>
            <p className="mt-5 text-lg leading-relaxed text-foreground/80">{trip.overview}</p>
          </div>

          <CreatorProfileBlock trip={trip} />

          {trip.highlights?.length > 0 && (
            <div>
              <h2 className="font-heading text-3xl mb-4">Trip Highlights</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {trip.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-xl bg-muted/60 p-3">
                    <Star className="h-5 w-5 text-primary shrink-0 mt-0.5" /><span>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {trip.itinerary?.length > 0 && (
            <div>
              <h2 className="font-heading text-3xl mb-4">Day-wise Itinerary</h2>
              <Accordion type="single" collapsible defaultValue="d0" className="space-y-2">
                {trip.itinerary.map((it, i) => (
                  <AccordionItem key={i} value={`d${i}`} className="border rounded-xl px-4" data-testid={`itinerary-day-${it.day}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <span className="flex items-center gap-3 text-left">
                        <span className="h-9 w-9 shrink-0 grid place-items-center rounded-full bg-primary/10 text-primary font-mono text-sm">D{it.day}</span>
                        <span className="font-heading text-xl">{it.title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pl-12">{it.description}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-6">
            {trip.inclusions?.length > 0 && (
              <div>
                <h3 className="font-heading text-2xl mb-3">Inclusions</h3>
                <ul className="space-y-2">{trip.inclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" /> {x}</li>)}</ul>
              </div>
            )}
            {trip.exclusions?.length > 0 && (
              <div>
                <h3 className="font-heading text-2xl mb-3">Exclusions</h3>
                <ul className="space-y-2">{trip.exclusions.map((x, i) => <li key={i} className="flex items-start gap-2 text-sm"><X className="h-4 w-4 text-destructive shrink-0 mt-0.5" /> {x}</li>)}</ul>
              </div>
            )}
          </div>

          {trip.important_info?.length > 0 && (
            <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-5">
              <h3 className="font-heading text-2xl mb-3 flex items-center gap-2"><Info className="h-5 w-5 text-amber-600 dark:text-amber-400" /> Important Information</h3>
              <ul className="space-y-2">{trip.important_info.map((x, i) => <li key={i} className="text-sm text-foreground/80">• {x}</li>)}</ul>
            </div>
          )}

          {trip.pickup_points?.length > 0 && (
            <div>
              <h3 className="font-heading text-2xl mb-3">Pickup Points</h3>
              <div className="flex flex-wrap gap-2">{(trip.pickups || []).map((p, i) => <Badge key={i} variant="outline" className="rounded-full py-1.5 px-3" data-testid="detail-pickup"><MapPinned className="h-3.5 w-3.5 mr-1" />{p.name}{p.extra > 0 ? <span className="ml-1 text-primary">+{inr(p.extra)}</span> : p.extra < 0 ? <span className="ml-1 text-emerald-600 dark:text-emerald-400">−{inr(Math.abs(p.extra))}</span> : null}</Badge>)}</div>
              <p className="text-xs text-muted-foreground mt-2">Price difference is per person, added to the sharing price.</p>
            </div>
          )}

          {trip.faqs?.length > 0 && (
            <div>
              <h2 className="font-heading text-3xl mb-4">FAQs</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {trip.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="border rounded-xl px-4">
                    <AccordionTrigger className="hover:no-underline font-medium text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <div id="reviews"><TripReviews trip={trip} /></div>
        </div>

        {/* Mobile sticky book bar */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3" data-testid="mobile-book-bar">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-muted-foreground leading-none">From · per person</p>
          <p className="font-heading text-2xl text-primary leading-tight">{inr(trip.starting_price)}</p>
        </div>
        <LeadDialog trip={trip}>
          <Button variant="outline" size="icon" className="h-11 w-11 rounded-full border-[#25D366] text-[#25D366]" data-testid="mobile-query"><MessageCircle className="h-5 w-5" /></Button>
        </LeadDialog>
        <BookingDialog trip={trip}>
          <Button className="h-11 rounded-full px-6" data-testid="mobile-book-now" disabled={trip.departure_count > 0 && !trip.bookable}>{trip.departure_count > 0 && !trip.bookable ? "Sold Out" : "Book Now"}</Button>
        </BookingDialog>
      </div>

      {/* Sticky sidebar */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-lg">
              <p className="text-sm text-muted-foreground">Starting from</p>
              <p className="font-heading text-4xl text-primary">{inr(trip.starting_price)}</p>
              <p className="text-xs text-muted-foreground">per person · Quad sharing · advance {inr(settings.advance_per_person || 1000)}/person</p>
              {trip.prices && (
                <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-testid="sharing-prices">
                  {[["quad", "Quad"], ["triple", "Triple"], ["double", "Double"]].map(([k, l]) => (
                    <div key={k} className="rounded-lg bg-muted/60 p-2" data-testid={`price-${k}`}>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1"><BedDouble className="h-3 w-3" /> {l}</p>
                      <p className="font-heading text-lg leading-tight">{inr(trip.prices[k])}</p>
                    </div>
                  ))}
                </div>
              )}

              {trip.departures?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Upcoming Departures</p>
                  <div className="space-y-2 max-h-44 overflow-auto no-scrollbar">
                    {trip.departures.map((d) => (
                      <div key={d.id} className={`flex items-center justify-between rounded-lg bg-muted/60 px-3 py-2 text-sm ${d.sold_out ? "opacity-60" : ""}`} data-testid="detail-departure">
                        <span className="flex flex-col">
                          <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4 text-primary" /> {d.date}</span>
                          {d.sold_out ? <span className="text-[11px] text-destructive font-medium ml-5">Sold out</span>
                            : d.seats_left != null && d.seats_left <= 5 ? <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium ml-5" data-testid="detail-seats-left">Only {d.seats_left} left</span>
                            : d.seats_left != null ? <span className="text-[11px] text-muted-foreground ml-5">{d.seats_left} seats</span> : null}
                        </span>
                        <span className="font-medium text-right"><span className="text-[10px] text-muted-foreground block leading-none">from</span>{inr(d.from_price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 space-y-2">
                <BookingDialog trip={trip}>
                  <Button className="w-full rounded-full h-11 text-base" data-testid="detail-book-now" disabled={trip.departure_count > 0 && !trip.bookable}>{trip.departure_count > 0 && !trip.bookable ? "Sold Out" : "Book Now"}</Button>
                </BookingDialog>
                <LeadDialog trip={trip}>
                  <Button variant="outline" className="w-full rounded-full h-11 border-[#25D366] text-[#1da851] hover:bg-[#25D366]/10 hover:text-[#1da851]" data-testid="detail-query-whatsapp">
                    <MessageCircle className="mr-1 h-4 w-4" /> Query on WhatsApp
                  </Button>
                </LeadDialog>
                {trip.pdf_url ? (
                  <a href={fileUrl(trip.pdf_url)} target="_blank" rel="noreferrer" data-testid="detail-download-pdf">
                    <Button variant="ghost" className="w-full rounded-full h-11 text-primary"><Download className="mr-1 h-4 w-4" /> Download Itinerary PDF</Button>
                  </a>
                ) : (
                  <Button variant="ghost" disabled className="w-full rounded-full h-11 opacity-50" data-testid="detail-download-pdf-disabled"><Download className="mr-1 h-4 w-4" /> Itinerary PDF coming soon</Button>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
